import React, { useState, useEffect } from 'react';
import { shippersAPI } from '../../services/api';
import Table from '../Common/Table';
import Modal from '../Common/Modal';

const ShipperManagement = () => {
  const [shippers, setShippers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingShipper, setEditingShipper] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'USA',
    tax_id: '',
    is_active: true,
  });

  useEffect(() => {
    loadShippers();
  }, []);

  const loadShippers = async () => {
    try {
      setLoading(true);
      const data = await shippersAPI.getAll();
      setShippers(data);
    } catch (error) {
      alert('Error loading shippers: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingShipper) {
        await shippersAPI.update(editingShipper.id, formData);
      } else {
        await shippersAPI.create(formData);
      }
      setShowModal(false);
      setEditingShipper(null);
      resetForm();
      loadShippers();
    } catch (error) {
      alert('Error saving shipper: ' + error.message);
    }
  };

  const handleEdit = (shipper) => {
    setEditingShipper(shipper);
    setFormData({
      company_name: shipper.company_name || '',
      contact_person: shipper.contact_person || '',
      email: shipper.email || '',
      phone: shipper.phone || '',
      address: shipper.address || '',
      city: shipper.city || '',
      state: shipper.state || '',
      zip_code: shipper.zip_code || '',
      country: shipper.country || 'USA',
      tax_id: shipper.tax_id || '',
      is_active: shipper.is_active !== undefined ? shipper.is_active : true,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this shipper?')) return;
    try {
      await shippersAPI.delete(id);
      loadShippers();
    } catch (error) {
      alert('Error deleting shipper: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      company_name: '',
      contact_person: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'USA',
      tax_id: '',
      is_active: true,
    });
  };

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'company_name', label: 'Company Name' },
    { key: 'contact_person', label: 'Contact Person' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'is_active', label: 'Active', format: (val) => val ? 'Yes' : 'No' },
  ];

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Shipper Management</h1>
        <button className="btn-primary" onClick={() => { setEditingShipper(null); resetForm(); setShowModal(true); }}>
          Add New Shipper
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <Table
          data={shippers}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      <Modal
        show={showModal}
        onClose={() => { setShowModal(false); setEditingShipper(null); resetForm(); }}
        title={editingShipper ? 'Edit Shipper' : 'Add New Shipper'}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Company Name *</label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              required
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Contact Person</label>
              <input
                type="text"
                value={formData.contact_person}
                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Tax ID</label>
              <input
                type="text"
                value={formData.tax_id}
                onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Address</label>
            <textarea
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              rows="2"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>City</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>State</label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Zip Code</label>
              <input
                type="text"
                value={formData.zip_code}
                onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Country</label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              />
            </div>
          </div>
          {editingShipper && (
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                Active
              </label>
            </div>
          )}
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => { setShowModal(false); setEditingShipper(null); resetForm(); }}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">Save</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ShipperManagement;


